import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { addUniversalTransitionEnd, getVariantPath, removeUniversalTransitionEnd } from '@uswitch/spark.utils';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import mergeRefs from 'react-merge-refs';
import { useOverlayManager } from './overlay-manager';
import { jsx as _jsx } from "theme-ui/jsx-runtime";
import { jsxs as _jsxs } from "theme-ui/jsx-runtime";
var wrapperVariantPath = getVariantPath({
  id: 'overlay',
  element: 'wrapper'
});
var overlayVariantPath = getVariantPath({
  id: 'overlay',
  element: 'overlay'
});
export var OverlayContext = /*#__PURE__*/React.createContext({
  active: false
});
/**
 * Overlay is a layer that covers and fades a pages content to give prominence to other content or another component.
 *
 */

export var Overlay = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _ref$as = _ref.as,
      As = _ref$as === void 0 ? 'div' : _ref$as,
      enabled = _ref.enabled,
      transitionTimeout = _ref.transitionTimeout,
      variant = _ref.variant,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, ["as", "enabled", "transitionTimeout", "variant", "children"]);

  var _React$useState = React.useState(),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isMounted = _React$useState2[0],
      setIsMounted = _React$useState2[1];

  var _React$useState3 = React.useState(),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      shouldRender = _React$useState4[0],
      setShouldRender = _React$useState4[1];

  var _React$useState5 = React.useState(),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      renderDiv = _React$useState6[0],
      setRenderDiv = _React$useState6[1];

  var wrapperDivRef = React.useRef();
  var timerRef = React.useRef();
  var transitionendCbRef = React.useRef();

  if (!transitionendCbRef.current) {
    transitionendCbRef.current = function () {
      setShouldRender(false);

      if (wrapperDivRef.current) {
        removeUniversalTransitionEnd(wrapperDivRef.current, transitionendCbRef.current);
      }
    };
  }

  useEffect(function () {
    if (enabled && !shouldRender) {
      setShouldRender(enabled);
    } else if (!enabled && shouldRender) {
      if (!wrapperDivRef.current) {
        setShouldRender(false);
        return;
      }

      if (!addUniversalTransitionEnd(wrapperDivRef.current, transitionendCbRef.current)) {
        timerRef.current = setTimeout(function () {
          timerRef.current = undefined;
          setShouldRender(false);
        }, transitionTimeout);
        return function () {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
          }
        };
      }
    }
  }, [enabled, shouldRender, transitionTimeout]); // use ref to keep the cb function unchanged over renders since it's used as component identity

  var cbRef = React.useRef(function (newRenderDiv) {
    setRenderDiv(newRenderDiv);
  });
  useOverlayManager(cbRef.current, shouldRender);
  useEffect(function () {
    if (renderDiv && shouldRender) {
      setIsMounted(true);
    } else {
      setIsMounted(false);
    }
  }, [renderDiv, shouldRender]);
  var active = isMounted && enabled;
  var opacity = {};
  if (!active) opacity.opacity = 0;

  if (renderDiv && shouldRender) {
    return /*#__PURE__*/ReactDOM.createPortal(_jsxs(As, _objectSpread(_objectSpread({
      sx: {
        variant: wrapperVariantPath(variant)
      }
    }, props), {}, {
      ref: mergeRefs([wrapperDivRef, ref]),
      children: [_jsx("div", {
        sx: _objectSpread({
          variant: overlayVariantPath(variant)
        }, opacity)
      }), _jsx(OverlayContext.Provider, {
        value: {
          active: active
        },
        children: children
      })]
    })), renderDiv);
  }

  return null;
});
Overlay.propTypes = {
  /**
   * Whether the overlay is enabled, changes to this property trigger transition animations
   */
  enabled: PropTypes.bool,

  /**
   * Timeout for (length of) transition animation timeout, used as fallback for browsers which
   * don't support any transition end event variant
   */
  transitionTimeout: PropTypes.number,
  variant: PropTypes.oneOf(['grey', 'white'])
};
Overlay.defaultProps = {
  enabled: true,
  transitionTimeout: 500,
  variant: 'grey'
};